import {
  GlobalNotification,
  Logo,
  MainLayout,
  NavItemType,
  ProxySession,
  useNotifications,
} from '@armhr/ui';
import { useAuth0 } from '@auth0/auth0-react';
import { useCallback, useEffect, useState } from 'react';

import { useApi } from '../../../hooks/useApi';
import { useUser } from '../../../hooks/useUser';
import OnboardingUnfinishedQuickhire from '../../../pages/onboarding/OnboardingUnfinishedQuickhire';
import { generateNavItems } from '../../../utils/navItems';
import { isProxying } from '../../../utils/proxy';
import { isUnfinishedQuickhire } from '../../../utils/user';
import Footer from './Footer';
import HeaderContent from './Header/HeaderContent';

const HCMMainLayoutWrapper: React.FC = () => {
  const { user } = useUser();
  if (isUnfinishedQuickhire(user)) {
    return <OnboardingUnfinishedQuickhire />;
  }
  return <HCMMainLayout />;
};

const HCMMainLayout: React.FC = () => {
  const api = useApi();
  const notifications = useNotifications();
  const { user, entity, company, loading } = useUser();
  const { user: auth0User, logout } = useAuth0();
  const [proxySession, setProxySession] = useState<ProxySession | null>(null);

  // Fetch proxy session for admin users who are proxying
  const fetchProxySession = useCallback(async () => {
    if (auth0User && isProxying(auth0User)) {
      try {
        const response = await api.admin.getProxySession();
        setProxySession(response.results);
      } catch (error) {
        console.error('Failed to fetch proxy session:', error);
        setProxySession(null);
      }
    }
  }, [api, auth0User]);

  useEffect(() => {
    fetchProxySession();
  }, [fetchProxySession]);

  // Only generate nav items when we have complete data
  const [navItems, setNavItems] = useState<{ items: NavItemType[] }>({
    items: generateNavItems(user!, company!, api, notifications, entity).items,
  });

  // Re-generate nav items when user or company changes, but only if we have complete data
  useEffect(() => {
    if (!loading && user && company) {
      setNavItems({
        items: generateNavItems(user, company, api, notifications, entity)
          .items as NavItemType[],
      });
    }
  }, [user, company, entity, api, notifications, loading]);

  const logoEnabled = company?.config?.find(
    (config) => config.flag === 'show_company_logo'
  )?.value;

  // HCM sidebar sits on a light (warm-white) surface, so use the
  // `executive` variant: Trust outer + Authority inner chevrons with
  // the deep-navy Executive wordmark for legibility against the panel.
  const logo =
    logoEnabled && company?.logo_url ? (
      <img
        src={company?.logo_url}
        alt="Company Logo"
        style={{ height: '65px' }}
      />
    ) : (
      <Logo variant="executive" showText={true} />
    );

  // Mini-drawer rail uses a smaller chevron mark so the brand
  // doesn't dominate the narrow column.
  const logoClosed = <Logo variant="executive" showText={false} width={22} />;

  // The topbar (visible at the iPad/mobile breakpoint when the drawer
  // collapses into a Toolbar) sits on the warm-white header surface, so
  // it needs the `executive` variant — Trust outer + Authority inner
  // chevrons with the Executive (deep navy) wordmark — otherwise the
  // white-wordmark `executive-on-dark` variant used in the drawer would
  // disappear against the light header background.
  const headerLogo =
    logoEnabled && company?.logo_url ? (
      <img
        src={company?.logo_url}
        alt="Company Logo"
        style={{ height: '32px' }}
      />
    ) : (
      <Logo variant="executive" showText={true} height={28} />
    );

  // Note: We don't provide onEndProxySession because the HCM app doesn't have
  // the ability to end proxy sessions - that's managed through the ops app.
  // The timer is shown in compact mode without the end button.
  return (
    <MainLayout
      headerContent={<HeaderContent />}
      inactivityPromptBeforeIdle={60 * 1000} // 1 minute
      inactivityTimeout={120 * 60 * 1000} // 2 hours
      enableAdminProxyBanner={true}
      logo={logo}
      logoClosed={logoClosed}
      headerLogo={headerLogo}
      navItems={navItems}
      notification={<GlobalNotification />}
      footerContent={<Footer />}
      onLogout={logout}
      proxySession={proxySession}
    />
  );
};

export default HCMMainLayoutWrapper;
